import { Component, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  CreditEstimateBasis,
  CreditPricing,
  CreditPricingActivity,
  CreditService,
} from '../../../services/credit.service';
import { ModelCatalogProvider, TutorService } from '../../../services/tutor.service';

/** The handful of activities an operator actually needs explained. The price
 *  list has 23 rows including seven impulse variants — showing all of them is
 *  noise. Prices still come from the backend, so they never drift. */
const ESSENTIALS = [
  'qa_answer',
  'exercise_interaction',
  'guided_step',
  'material_generation',
  // Creator: `course_creation` was the flat 35-credit line that nothing ever
  // charged; migration 041 replaced it with per-step prices and set it
  // inactive. Since `/credits/pricing` only returns active rows, keeping it
  // here showed the customer NO creator price at all while the creator was
  // billing them. `canvas_course_structure` is the step that actually runs
  // when a course is generated.
  'canvas_course_structure',
];

/** All impulse variants cost the same; they are collapsed into one row. */
const IMPULSE_PREFIX = 'impulse_';

/** Proactive impulses fire on their own (scroll/dwell), so there is no
 *  meaningful slider for them — they scale with learners. Estimated per
 *  learner per month, shown transparently in the breakdown. */
const IMPULSES_PER_LEARNER = 4;

const FALLBACK_LEARNERS = 50;

/** Most agents a customer compares side by side; more is a spreadsheet. */
const MAX_AGENTS = 6;

/** Used when the model catalog cannot be loaded: the standard model, factor 1. */
const STANDARD_PROVIDER = 'OpenAI';

/** Slider ranges the customer picks from; a typed value above the current
 *  range moves it up. Customers with thousands of learners or courses need
 *  more than the old fixed 1,000. */
const LEARNER_RANGES = [1_000, 10_000, 100_000, 1_000_000];
const COURSE_RANGES = [100, 1_000, 10_000];

/** Activities that turn a course into agent knowledge. All run on our own
 *  orchestration model (factor 1, never the customer's agent model):
 *  - `course_structure_parse` once per analysed course,
 *  - `guided_compile` once per lesson script (only changed units are rebuilt),
 *  - `media_enrichment` once per image or package in a sync,
 *  - `media_transcription` per started minute of audio/video.
 *  Embedding files is free (`kb_embed` = 0). */
const COURSE_ACTIVITIES = {
  structure: 'course_structure_parse',
  unit: 'guided_compile',
  media: 'media_enrichment',
  minute: 'media_transcription',
};

/** Defaults where the basis knows nothing about the courses. */
const DEFAULT_COURSE = { courses: 5, units: 8, media: 10, minutes: 0 };

/**
 * Three ready-made cases, each fully editable. They differ in how many of the
 * known learners are active, how much an active learner uses the agent, and
 * how much of the course content changes per month. Where the last 30 days
 * show real usage, `usageFactor` scales that instead of the fixed rates.
 */
type ScenarioKey = 'cautious' | 'expected' | 'intensive';

interface ScenarioPreset {
  /** Share of the learners known from the LMS that use an agent in a month. */
  activeShare: number;
  questions: number;
  exercises: number;
  /** Multiplies the measured per-learner usage of the last 30 days. */
  usageFactor: number;
  materials: number;
  updateRatePct: number;
}

const SCENARIO_PRESETS: Record<ScenarioKey, ScenarioPreset> = {
  cautious: { activeShare: 0.3, questions: 8, exercises: 2, usageFactor: 0.5, materials: 2, updateRatePct: 5 },
  expected: { activeShare: 0.6, questions: 20, exercises: 5, usageFactor: 1, materials: 5, updateRatePct: 10 },
  intensive: { activeShare: 0.9, questions: 40, exercises: 12, usageFactor: 2, materials: 15, updateRatePct: 25 },
};
const SCENARIO_KEYS: ScenarioKey[] = ['cautious', 'expected', 'intensive'];

interface Segment {
  key: string;
  label: string;
  credits: number;
  color: string;
}

/** One agent in the estimate: its model and its own usage. */
interface AgentRow {
  id: number;
  /** The customer's agent this row stands for, if it came from the basis. */
  name: string | null;
  provider: string;
  learners: number;
  /** Upper end of the learner slider (one of LEARNER_RANGES). */
  learnerRange: number;
  questionsPerLearner: number;
  exercisesPerLearner: number;
}

/** Courses kept as agent knowledge: what setting them up costs once, and
 *  what keeping them current costs per month at a given rate of change. */
interface CourseUpkeep {
  courses: number;
  courseRange: number;
  unitsPerCourse: number;
  mediaPerCourse: number;
  videoMinutesPerCourse: number;
  /** Share of the course content that changes per month, in percent. */
  updateRatePct: number;
}

/** Everything one case is calculated from. */
interface Scenario {
  key: ScenarioKey;
  agents: AgentRow[];
  materialsPerMonth: number;
  course: CourseUpkeep;
}

/** A selectable model with the credit factor the billing applies to it. */
interface ModelOption {
  provider: string;
  label: string;
  factor: number;
  speed?: 'fast' | 'ultrafast' | null;
}

/**
 * Explains credits and estimates the monthly bill in three editable cases —
 * per agent, each with its own model, plus keeping the courses current.
 *
 * The token conversion is internal and never shown. Prices and the plan
 * allowance come from `/credits/pricing`; the model list and each model's
 * credit factor from `/model-catalog` — the same function the charge path
 * uses, so the estimate cannot drift from the bill. What we already know
 * about the customer (learners and courses from the LMS, their agents and
 * models, the last 30 days of use) comes from `/credits/estimate-basis` and
 * seeds the three cases. The factor applies to what an agent does (chat,
 * exercises, impulses); teaching material is made in the creator and course
 * preparation runs on our own model (factor 1 for both).
 */
@Component({
  selector: 'app-credits-info-dialog',
  templateUrl: './credits-info-dialog.component.html',
  styleUrl: './credits-info-dialog.component.scss',
})
export class CreditsInfoDialogComponent implements OnInit {
  pricing: CreditPricing | null = null;
  basis: CreditEstimateBasis | null = null;
  loading = true;
  error = false;

  private nextAgentId = 1;
  scenarios: Scenario[] = SCENARIO_KEYS.map((k) => this.seedScenario(k));
  /** The case being edited and shown in detail. */
  active: Scenario = this.scenarios[1];

  models: ModelOption[] = [];
  readonly maxAgents = MAX_AGENTS;
  readonly learnerRanges = LEARNER_RANGES;
  readonly courseRanges = COURSE_RANGES;
  readonly impulsesPerLearner = IMPULSES_PER_LEARNER;

  constructor(
    protected dialogRef: NbDialogRef<CreditsInfoDialogComponent>,
    private creditService: CreditService,
    private tutorService: TutorService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.creditService.getPricing().subscribe({
      next: (pricing) => {
        this.pricing = pricing;
        this.loading = false;
        this.reseedAll();
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });
    // Without the basis the cases start from the plan's guidance value.
    this.creditService.getEstimateBasis().subscribe({
      next: (basis) => {
        this.basis = basis;
        this.reseedAll();
      },
      error: () => {
        this.basis = null;
      },
    });
    // The estimate still works without the catalog — on the standard model.
    this.tutorService.fetchModelCatalog().subscribe({
      next: (catalog) => {
        this.models = [...catalog.providers]
          .sort((a, b) => b.credit_multiplier - a.credit_multiplier)
          .map((p) => this.toOption(p));
      },
      error: () => {
        this.models = [];
      },
    });
  }

  // ─── Cases: seeded from what we know, then freely edited ──────────────────

  /** Learners known for this customer: the larger of the LMS roster and the
   *  learners active in the last 30 days (a roster can lag behind — LTI has
   *  none at all), else the plan's guidance value. */
  get knownLearners(): number {
    const measured = Math.max(this.basis?.learners_known ?? 0, this.basis?.learners_active_30d ?? 0);
    return measured || this.pricing?.plan.guidance_user_count || FALLBACK_LEARNERS;
  }

  /** Courses in the LMS beyond those already kept as knowledge, if any. */
  get knownCoursesBeyond(): number | null {
    const c = this.basis?.courses;
    return c?.known && c.known > c.count ? c.known : null;
  }

  private seedScenario(key: ScenarioKey): Scenario {
    const p = SCENARIO_PRESETS[key];
    const usage = this.basis?.usage_30d;
    const rate = (measured: number | null | undefined, fallback: number) =>
      measured != null && measured > 0 ? Math.max(1, Math.round(measured * p.usageFactor)) : fallback;
    const questions = rate(usage?.questions_per_active_learner, p.questions);
    const exercises = rate(usage?.exercises_per_active_learner, p.exercises);
    const activeLearners = Math.round(this.knownLearners * p.activeShare);

    // One row per known agent (most used first), its learners by its share
    // of the use; without known agents, one row on the standard model.
    const known = (this.basis?.agents ?? []).slice(0, MAX_AGENTS);
    const agents: AgentRow[] = known.length
      ? known.map((a) =>
          this.newAgent(Math.round(activeLearners * a.share), {
            name: a.name,
            provider: this.knownProvider(a.provider),
            questions,
            exercises,
          })
        )
      : [this.newAgent(activeLearners, { questions, exercises })];

    // Courses kept as knowledge today (all LMS courses if none is connected
    // yet); the intensive case assumes every course of the LMS.
    const c = this.basis?.courses;
    const base = c?.count || c?.known || DEFAULT_COURSE.courses;
    const courses = key === 'intensive' ? Math.max(base, c?.known ?? 0) : base;
    return {
      key,
      agents,
      materialsPerMonth: p.materials,
      course: {
        courses,
        courseRange: fitRange(COURSE_RANGES, courses),
        unitsPerCourse: c?.units_per_course ?? DEFAULT_COURSE.units,
        mediaPerCourse: c?.media_per_course ?? DEFAULT_COURSE.media,
        videoMinutesPerCourse: c?.video_minutes_per_course ?? DEFAULT_COURSE.minutes,
        updateRatePct: p.updateRatePct,
      },
    };
  }

  /** Rebuild all cases from the current data — keeps the selected case. */
  private reseedAll(): void {
    const key = this.active.key;
    this.scenarios = SCENARIO_KEYS.map((k) => this.seedScenario(k));
    this.active = this.scenarios.find((s) => s.key === key)!;
  }

  /** Back to the suggestion for the case being edited. */
  resetActive(): void {
    const fresh = this.seedScenario(this.active.key);
    this.scenarios = this.scenarios.map((s) => (s.key === fresh.key ? fresh : s));
    this.active = fresh;
  }

  select(s: Scenario): void {
    this.active = s;
  }

  /** What the cases were seeded from, for the line above them. */
  get basisSource(): string | null {
    return this.basis?.source ?? null;
  }

  trackScenario = (_: number, s: Scenario) => s.key;

  // ─── Agents and models ────────────────────────────────────────────────────

  private newAgent(
    learners: number,
    opts: { name?: string | null; provider?: string; questions?: number; exercises?: number } = {}
  ): AgentRow {
    return {
      id: this.nextAgentId++,
      name: opts.name ?? null,
      provider: opts.provider ?? STANDARD_PROVIDER,
      learners,
      learnerRange: fitRange(LEARNER_RANGES, learners),
      questionsPerLearner: opts.questions ?? SCENARIO_PRESETS.expected.questions,
      exercisesPerLearner: opts.exercises ?? SCENARIO_PRESETS.expected.exercises,
    };
  }

  /** A provider from the basis, if the calculator can price it. */
  private knownProvider(provider: string | null | undefined): string {
    if (!provider) return STANDARD_PROVIDER;
    if (!this.models.length) return provider;
    return this.models.some((m) => m.provider === provider) ? provider : STANDARD_PROVIDER;
  }

  private toOption(p: ModelCatalogProvider): ModelOption {
    const key = `pages.tutors.modelSelector.providers.${p.provider}.name`;
    const name = this.translate.instant(key);
    return {
      provider: p.provider,
      label: `${name === key ? p.provider : name} · ${p.model_label || p.model}`,
      factor: p.credit_multiplier,
      speed: p.speed,
    };
  }

  addAgent(): void {
    const agents = this.active.agents;
    if (agents.length >= MAX_AGENTS) return;
    const last = agents[agents.length - 1];
    agents.push(
      this.newAgent(last.learners, {
        questions: last.questionsPerLearner,
        exercises: last.exercisesPerLearner,
      })
    );
  }

  removeAgent(agent: AgentRow): void {
    if (this.active.agents.length <= 1) return;
    this.active.agents = this.active.agents.filter((a) => a !== agent);
  }

  /** Credit factor of an agent's model (1 while the catalog is not there). */
  factorOf(agent: AgentRow): number {
    return this.models.find((m) => m.provider === agent.provider)?.factor ?? 1;
  }

  /** What one agent costs per month: its model factor on chat, exercises
   *  and the impulses its learners trigger. */
  agentCredits(agent: AgentRow): number {
    return this.factorOf(agent) * this.agentBaseCredits(agent);
  }

  private agentBaseCredits(agent: AgentRow): number {
    return (
      agent.learners * agent.questionsPerLearner * this.priceOf('qa_answer') +
      agent.learners * agent.exercisesPerLearner * this.priceOf('exercise_interaction') +
      agent.learners * IMPULSES_PER_LEARNER * this.impulsePrice
    );
  }

  trackAgent = (_: number, a: AgentRow) => a.id;
  trackModel = (_: number, m: ModelOption) => m.provider;

  // ─── Ranges: pick the slider's upper end, or type a value ─────────────────

  /** A typed or dragged learner count; a value above the range moves it up. */
  setLearners(agent: AgentRow, value: unknown): void {
    agent.learners = clampCount(value, LEARNER_RANGES[LEARNER_RANGES.length - 1]);
    if (agent.learners > agent.learnerRange) {
      agent.learnerRange = fitRange(LEARNER_RANGES, agent.learners);
    }
  }

  /** Switching to a smaller range pulls the value into it. */
  setLearnerRange(agent: AgentRow, range: number): void {
    agent.learnerRange = range;
    agent.learners = Math.min(agent.learners, range);
  }

  setCourses(value: unknown): void {
    const c = this.active.course;
    c.courses = clampCount(value, COURSE_RANGES[COURSE_RANGES.length - 1]);
    if (c.courses > c.courseRange) {
      c.courseRange = fitRange(COURSE_RANGES, c.courses);
    }
  }

  setCourseRange(range: number): void {
    const c = this.active.course;
    c.courseRange = range;
    c.courses = Math.min(c.courses, range);
  }

  /** Slider step: 100 positions across the range. */
  stepOf(range: number): number {
    return Math.max(1, range / 100);
  }

  // ─── Prices (credits per unit, from the backend) ──────────────────────────

  private priceOf(activityType: string): number {
    const a = this.pricing?.activities.find((x) => x.activity_type === activityType);
    return a ? a.credits_milli / 1000 : 0;
  }

  /** One representative impulse — all variants share the same price. */
  private get impulsePrice(): number {
    const a = this.pricing?.activities.find((x) =>
      x.activity_type.startsWith(IMPULSE_PREFIX)
    );
    return a ? a.credits_milli / 1000 : 0;
  }

  /** Curated price list: the essentials plus one merged impulse row. */
  get essentialPrices(): { label: string; credits: number }[] {
    if (!this.pricing) return [];
    const rows = ESSENTIALS.map((key) => {
      const a = this.pricing!.activities.find((x) => x.activity_type === key);
      return a ? { label: a.display_name, credits: a.credits_milli / 1000 } : null;
    }).filter((r): r is { label: string; credits: number } => !!r);
    return rows;
  }

  get impulseCredits(): number {
    return this.impulsePrice;
  }

  /** Full price list, collapsed by default (Lasse 2026-10-01). */
  pricesOpen = false;
  private priceGroupsFor: unknown = null;
  private priceGroupsCache: { key: string; rows: { label: string; credits: number }[] }[] = [];

  private groupOf(activityType: string): string {
    if (activityType.startsWith('canvas_')) return 'creator';
    if (activityType === 'grading') return 'dozent';
    if (
      /(_import|_parse|_compile)$/.test(activityType) ||
      activityType.startsWith('media_') ||
      activityType.startsWith('course_')
    ) {
      return 'content';
    }
    return 'chat';
  }

  /** Every priced activity the customer can trigger, grouped. The impulse
   *  variants share one price and show as one row; free rows are named in
   *  the note below instead. Same array instance while the data is the same. */
  get priceGroups(): { key: string; rows: { label: string; credits: number }[] }[] {
    if (!this.pricing) return [];
    if (this.priceGroupsFor === this.pricing) return this.priceGroupsCache;
    const order = ['chat', 'creator', 'content', 'dozent'];
    const groups = new Map<string, { label: string; credits: number }[]>(
      order.map((k) => [k, []])
    );
    for (const a of this.pricing.activities as CreditPricingActivity[]) {
      if (a.credits_milli <= 0 || a.activity_type.startsWith(IMPULSE_PREFIX)) continue;
      groups.get(this.groupOf(a.activity_type))!.push({
        label: a.display_name,
        credits: a.credits_milli / 1000,
      });
    }
    if (this.impulsePrice > 0) {
      groups.get('chat')!.push({
        label: this.translate.instant('pages.organisation.credits.info.seg.impulses'),
        credits: this.impulsePrice,
      });
    }
    this.priceGroupsFor = this.pricing;
    this.priceGroupsCache = order
      .map((key) => ({ key, rows: groups.get(key)! }))
      .filter((g) => g.rows.length);
    return this.priceGroupsCache;
  }

  // ─── Courses: setup once, keeping them current per month ──────────────────

  /** Credits to analyse and prepare one course once. */
  private perCourseCredits(c: CourseUpkeep): number {
    return (
      this.priceOf(COURSE_ACTIVITIES.structure) +
      c.unitsPerCourse * this.priceOf(COURSE_ACTIVITIES.unit) +
      c.mediaPerCourse * this.priceOf(COURSE_ACTIVITIES.media) +
      c.videoMinutesPerCourse * this.priceOf(COURSE_ACTIVITIES.minute)
    );
  }

  /** One-time: every course analysed and prepared once. Not part of the
   *  monthly total — shown next to it. */
  setupCreditsOf(s: Scenario): number {
    return s.course.courses * this.perCourseCredits(s.course);
  }

  /** Per month: the changed share is analysed and prepared again. Only
   *  changed units and media are rebuilt, so the rate scales the setup. */
  upkeepCreditsOf(s: Scenario): number {
    return this.setupCreditsOf(s) * (s.course.updateRatePct / 100);
  }

  get courseSetupCredits(): number {
    return this.setupCreditsOf(this.active);
  }

  get courseUpkeepCredits(): number {
    return this.upkeepCreditsOf(this.active);
  }

  // ─── Estimate ─────────────────────────────────────────────────────────────

  /** Net EUR for a credit amount (0 while the rate is unknown). */
  eurOf(credits: number): number {
    const perEur = this.pricing?.credits_per_eur ?? 0;
    return perEur ? credits / perEur : 0;
  }

  get impulseCount(): number {
    return this.active.agents.reduce((n, a) => n + a.learners * IMPULSES_PER_LEARNER, 0);
  }

  /** Sum over a case's agents of one cost part, each weighted with its factor. */
  private sumAgents(s: Scenario, part: (a: AgentRow) => number): number {
    return s.agents.reduce((sum, a) => sum + this.factorOf(a) * part(a), 0);
  }

  /** Cost split by category — drives the bar and the legend. */
  segmentsOf(s: Scenario): Segment[] {
    return [
      {
        key: 'chat',
        label: 'chat',
        credits: this.sumAgents(s, (a) => a.learners * a.questionsPerLearner * this.priceOf('qa_answer')),
        color: '#2563eb',
      },
      {
        key: 'exercises',
        label: 'exercises',
        credits: this.sumAgents(
          s,
          (a) => a.learners * a.exercisesPerLearner * this.priceOf('exercise_interaction')
        ),
        color: '#00a35c',
      },
      {
        key: 'impulses',
        label: 'impulses',
        credits: this.sumAgents(s, (a) => a.learners * IMPULSES_PER_LEARNER * this.impulsePrice),
        color: '#8b5cf6',
      },
      {
        key: 'materials',
        label: 'materials',
        credits: s.materialsPerMonth * this.priceOf('material_generation'),
        color: '#f59e0b',
      },
      {
        key: 'courses',
        label: 'courses',
        credits: this.upkeepCreditsOf(s),
        color: '#0891b2',
      },
    ].filter((seg) => seg.credits > 0);
  }

  totalOf(s: Scenario): number {
    return this.segmentsOf(s).reduce((sum, seg) => sum + seg.credits, 0);
  }

  get segments(): Segment[] {
    return this.segmentsOf(this.active);
  }

  get totalCredits(): number {
    return this.totalOf(this.active);
  }

  /** Width of a bar segment in percent of the total. */
  segmentPercent(s: Segment): number {
    const total = this.totalCredits;
    return total > 0 ? (s.credits / total) * 100 : 0;
  }

  get includedCredits(): number | null {
    return this.pricing?.plan.included_credits ?? null;
  }

  get overageCredits(): number {
    const included = this.includedCredits;
    if (included === null) return 0;
    return Math.max(0, this.totalCredits - included);
  }

  /** Net EUR for whatever exceeds the plan allowance. */
  get overageEur(): number {
    return this.eurOf(this.overageCredits);
  }

  /** Net EUR of the whole estimate — what this usage is worth in credits
   *  bought, before any plan allowance. */
  get totalEur(): number {
    return this.eurOf(this.totalCredits);
  }

  get withinPlan(): boolean {
    return this.includedCredits !== null && this.overageCredits === 0;
  }

  /** Share of the plan allowance consumed, capped for the bar. */
  get planUsedPercent(): number | null {
    const included = this.includedCredits;
    if (!included) return null;
    return Math.min(100, Math.round((this.totalCredits / included) * 100));
  }

  trackSegment = (_: number, s: Segment) => s.key;

  close(): void {
    this.dialogRef.close();
  }
}

/** Whole, non-negative count from a typed or dragged value, capped. */
function clampCount(value: unknown, max: number): number {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

/** Smallest range that holds the value (the largest one if none does). */
function fitRange(ranges: number[], value: number): number {
  return ranges.find((r) => value <= r) ?? ranges[ranges.length - 1];
}
